import React, { useState, useEffect, useCallback } from 'react';
import './SmartBanner.css';

const FigmaErrorIcon = () => (
  <span className="smart-banner__figma-icon smart-banner__figma-icon--error" aria-hidden>
    <svg className="smart-banner__figma-icon-glyph" viewBox="0 0 12 12" fill="none">
      <path d="M3 3l6 6M9 3L3 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </span>
);

const FigmaSuccessIcon = () => (
  <span className="smart-banner__figma-icon smart-banner__figma-icon--success" aria-hidden>
    <svg className="smart-banner__figma-icon-glyph smart-banner__figma-icon-glyph--check" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6l2.5 2.5L9.5 3.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const FigmaWarningIcon = () => (
  <span className="smart-banner__figma-icon smart-banner__figma-icon--warning" aria-hidden>
    <svg className="smart-banner__figma-icon-glyph smart-banner__figma-icon-glyph--warn" viewBox="0 0 20 20" fill="white">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  </span>
);

const SmartBanner = ({
  type = 'info',
  message,
  title,
  duration = 5000,
  onClose,
  showIcon = true,
  position = 'top',
  className = '',
  /** 'figma-error' | 'figma-success' | 'figma-warning' — white card toast (top-right), matches Figma */
  variant,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const isFigmaError = variant === 'figma-error';
  const isFigmaSuccess = variant === 'figma-success';
  const isFigmaWarning = variant === 'figma-warning';
  const isFigma = isFigmaError || isFigmaSuccess || isFigmaWarning;

  const effectivePosition = isFigma ? 'top-right' : position;
  const effectiveType = isFigmaError
    ? 'error'
    : isFigmaSuccess
      ? 'success'
      : isFigmaWarning
        ? 'warning'
        : type;

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const getIcon = () => {
    if (!showIcon) return null;

    if (isFigmaError) {
      return <FigmaErrorIcon />;
    }
    if (isFigmaSuccess) {
      return <FigmaSuccessIcon />;
    }
    if (isFigmaWarning) {
      return <FigmaWarningIcon />;
    }

    switch (type) {
      case 'success':
        return (
          <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        if (effectivePosition === 'top-right') {
          return (
            <span className="smart-banner__error-dot" aria-hidden>
              <svg className="smart-banner__error-x" viewBox="0 0 12 12" fill="none">
                <path d="M3 3l6 6M9 3L3 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </span>
          );
        }
        return (
          <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const renderFigmaText = () => {
    if (isFigmaError) {
      return (
        <>
          <div className="smart-banner__figma-title">{title || 'Something went wrong'}</div>
          {message ? <div className="smart-banner__figma-body">{message}</div> : null}
        </>
      );
    }
    if (isFigmaSuccess) {
      if (title) {
        return (
          <>
            <div className="smart-banner__figma-title">{title}</div>
            {message ? <div className="smart-banner__figma-body">{message}</div> : null}
          </>
        );
      }
      return message ? <div className="smart-banner__figma-title">{message}</div> : null;
    }
    if (isFigmaWarning) {
      return message ? <div className="smart-banner__figma-title">{message}</div> : null;
    }
    return null;
  };

  if (!isVisible) return null;

  const figmaModifier = isFigmaError
    ? 'figma-error'
    : isFigmaSuccess
      ? 'figma-success'
      : isFigmaWarning
        ? 'figma-warning'
        : '';

  return (
    <div
      className={`smart-banner smart-banner--${effectiveType} smart-banner--${effectivePosition} ${
        isFigma ? `smart-banner--figma smart-banner--${figmaModifier}` : ''
      } ${isExiting ? 'smart-banner--exiting' : ''} ${className}`}
      role="alert"
      aria-live="polite"
    >
      {isFigma && <div className="smart-banner__figma-accent" aria-hidden />}

      <div className="smart-banner__content">
        {getIcon()}
        <div className="smart-banner__text">
          {isFigma ? (
            renderFigmaText()
          ) : (
            <>
              {title && <div className="smart-banner__title">{title}</div>}
              <div className="smart-banner__message">{message}</div>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        className={`smart-banner__close ${isFigma ? 'smart-banner__close--figma' : ''}`}
        onClick={handleClose}
        aria-label="Close banner"
      >
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {duration > 0 && !isFigma && (
        <div className="smart-banner__progress">
          <div className="smart-banner__progress-bar" style={{ animationDuration: `${duration}ms` }} />
        </div>
      )}
    </div>
  );
};

export default SmartBanner;
